import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import AgentHubService from 'services/AgentHubService';
import { AlertRecipient, SavedAgentSettings } from './types';
import { fmtTime } from './parts';

const service = new AgentHubService();
const same = (a: AlertRecipient[], b: AlertRecipient[]) => a.map(p => p.slackId).join() === b.map(p => p.slackId).join();

/**
 * Who the agent's Slack alerts @-mention ("Needs a person", a Notion page that didn't save). Saved here, not in its
 * agent.yaml: the hub applies it as soon as it's saved. Nobody chosen: the approvers, as before.
 */
export default function AlertRecipients({ agent }: { agent: string }) {
  const [saved, setSaved] = useState<SavedAgentSettings | null>(null);
  const [people, setPeople] = useState<AlertRecipient[]>([]);
  const [members, setMembers] = useState<AlertRecipient[] | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setSaved(null);
    setSettingsError(null);
    setPeopleError(null);
    setSaveError(null);
    service.settings(agent).then(s => { if (live) { setSaved(s); setPeople(s.alertRecipients ?? []); } }).catch(e => live && setSettingsError(`Settings not loaded: ${e.message}`));
    service.slackMembers().then(m => live && setMembers(m)).catch(e => live && setPeopleError(`Slack people not loaded: ${e.message}`));
    return () => { live = false; };
  }, [agent]);

  const save = () => {
    setSaving(true);
    setSaveError(null);
    service.saveAlertRecipients(agent, people)
      .then(s => { setSaved(s); setPeople(s.alertRecipients ?? []); })
      .catch(e => setSaveError(`Not saved: ${e.message}`))
      .finally(() => setSaving(false));
  };

  const changed = !!saved && !same(people, saved.alertRecipients ?? []);
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Alerts go to</Typography>
      {settingsError && <Alert severity="error" sx={{ mb: 1 }}>{settingsError}</Alert>}
      {peopleError && <Alert severity="error" sx={{ mb: 1 }}>{peopleError}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert>}
      {!saved && !settingsError && <CircularProgress size={18} />}
      {saved && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 320, flex: 1, maxWidth: 560 }}
            options={members ?? []}
            loading={!members && !peopleError}
            value={people}
            onChange={(_e, v) => setPeople(v)}
            getOptionLabel={o => o.name}
            isOptionEqualToValue={(o, v) => o.slackId === v.slackId}
            renderTags={(v, getTagProps) => v.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.slackId} />)}
            renderInput={params => <TextField {...params} placeholder={people.length ? '' : 'The approvers (default): pick people to send them to instead'} />}
          />
          <Button size="small" variant="contained" disabled={!changed || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
          {changed && <Button size="small" disabled={saving} onClick={() => setPeople(saved.alertRecipients ?? [])}>Undo</Button>}
        </Box>
      )}
      {saved && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {saved.alertRecipients?.length ? 'They are @-mentioned in #agent-ops when this agent needs a person.' : 'Nobody chosen: the approvers are @-mentioned, as set in the hub.'}
          {saved.updatedBy ? ` Last saved by ${saved.updatedBy}${saved.updatedAt ? `, ${fmtTime(saved.updatedAt)}` : ''}.` : ''} Applies to the next alert; no deploy needed.
        </Typography>
      )}
    </Box>
  );
}
